import React, { useEffect, useState } from 'react'
import CustomSelect, { CustomSelectOption } from '@components/CustomSelect'
import type {
  AppointmentInterviewResource,
  InterviewQuestionOptionsResource,
} from '@entities/api/Interview'
import dayjs from '@utils/dayjs'
import toCalendarDate from '@utils/toCalendarDate'
import Calendar from 'react-calendar'
import { useFormContext } from 'react-hook-form'

interface Props {
  prefix: string
  itemName: string | null
  appointment: AppointmentInterviewResource
  required: boolean
  options?: InterviewQuestionOptionsResource | null
}

const DateTime: React.FC<Props> = ({ prefix, itemName, appointment, required, options }) => {
  const today = dayjs().tz()
  const { register, watch, setValue } = useFormContext()
  const dateName = `${prefix}.datetime_value.date`
  const timeName = `${prefix}.datetime_value.time`
  const dateStr = watch(dateName)
  const [date, setDate] = useState(() => toCalendarDate(dateStr || today))
  const [time, setTime] = useState<string>(() => watch(timeName) || '')
  useEffect(() => {
    setValue(dateName, dayjs(date).format('YYYY-MM-DD'))
  }, [date])
  useEffect(() => {
    setValue(timeName, time)
  }, [time])

  const errorPrefix = `${itemName ? `${itemName}について、` : ''}`

  let maxJsDate
  switch (options?.max_date) {
    case 'today':
      maxJsDate = toCalendarDate(today)
      break
    case 'start_date':
      maxJsDate = toCalendarDate(appointment.start_date)
      break
    case 'day_before_start_date':
      maxJsDate = toCalendarDate(dayjs.tz(appointment.start_date).add(-1, 'day'))
      break
    default:
      maxJsDate = undefined
  }
  if (maxJsDate && date > maxJsDate) {
    setDate(maxJsDate)
  }
  let minJsDate
  switch (options?.min_date) {
    case 'today':
      minJsDate = toCalendarDate(today)
      break
    case 'start_date':
      minJsDate = toCalendarDate(appointment.start_date)
      break
    case 'day_after_start_date':
      minJsDate = toCalendarDate(dayjs.tz(appointment.start_date).add(1, 'day'))
      break
    default:
      minJsDate = undefined
  }
  if (minJsDate && date < minJsDate) {
    setDate(minJsDate)
  }

  const hours = Array.from({ length: 24 }, (value, index) => index)
  const hourOptions: CustomSelectOption[] = hours.map((hour) => ({
    value: `${hour}:00`,
    label: `${hour}時`,
  }))

  const handleChangeDate = (newDate) => {
    setDate(newDate)
  }

  return (
    <>
      <div className="row justify-content-center">
        <div className="col-auto">
          <Calendar
            value={date}
            minDate={minJsDate}
            maxDate={maxJsDate}
            onChange={handleChangeDate}
            className="zm-calendar"
            formatDay={(_locale, d) => dayjs(d).format('D')}
          />
          <input
            ref={register({
              required: required && `${errorPrefix}日付を入力してください`,
            })}
            type="hidden"
            name={dateName}
          />
        </div>
      </div>
      {options?.require_hour && (
        <div className="row justify-content-center py-4">
          <div className="col-auto form-inline">
            {dayjs(date).format('YYYY年M月D日')}
            <div className="input-group ml-3" data-testid="datetime-hour-select">
              <CustomSelect
                value={time}
                onChange={setTime}
                options={hourOptions}
                placeholder="お選びください"
                title="時間を選択"
              />
              <input
                ref={register({
                  required: required && `${errorPrefix}時間を選択してください`,
                })}
                type="hidden"
                name={timeName}
              />
              <div className="input-group-append">
                <span className="input-group-text border-0 bg-white">ごろ</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {!!options?.require_hour || <input ref={register} type="hidden" name={timeName} />}
    </>
  )
}

export default DateTime
